import React, { useCallback, useEffect, useState } from 'react';
import RightPopUp from '../../components/common/RightPopUp';
import { diseaseIdToName, IVaccineStatus, VaccineInfo } from '../../api/fetchVaccineSchedule';
import { track } from '../../tracking';
import { captureError } from '../captureError';
import AppLoader from '../../components/common/AppLoader';
import VaccinationChart from '../../components/VaccinationChart';
import { AP } from '../Appointments';
import moment from 'moment';
import { getDefaultClinicId } from '../../store/idbHandler';
import { notification } from 'antd';
import pushPxDraft from '../../api/pushPxDraft';
import { generatePrescriptionId } from '../../store/utils';
import fetchPxDraft from '../../api/fetchPxDraft';
import { Tool } from '../../api/pushVisit';

interface AddVaccinationRightPopUpProps {
    closeMe: () => void;
    appointment: AP;
    vaccinationData: Partial<VaccineInfo> | undefined;
    setVaccinationData: (vaccineChart: Partial<VaccineInfo> | undefined) => void;
}

const ApptAddVaccinationRightPopUp: React.FC<AddVaccinationRightPopUpProps> = ({
    closeMe,
    appointment,
    vaccinationData,
    setVaccinationData
}) => {
    const [isVaccinationDataLoading, setIsVaccinationDataLoading] = useState<boolean>(false);
    const [newVaccs, setNewVaccs] = useState<IVaccineStatus[]>([]);
    const [followUpVaccine, setFollowUpVaccine] = useState<IVaccineStatus | undefined>();
    const [vaccinationHistory, setVaccinationHistory] = useState<IVaccineStatus[] | undefined>(
        undefined,
    );
    const [draftTool, setDraftTool] = useState<Partial<Tool>>({});
    const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false);

    const dob = moment(appointment.patient.dob);
    const ageInYears = moment().diff(dob, 'years');

    const defaultSchedule = ageInYears ? (ageInYears <= 18 ? 'plan' : 'otherVaccs') : 'plan';

    const { patient_oid, doctor_oid, patient } = appointment;

    const fetchVaccineData = async () => {
        if (vaccinationData) return;
        setIsVaccinationDataLoading(true);
        try {
            const result = await fetch('/vaccination-data.json?v=11');

            const res = await result.json();

            setVaccinationData(res);
        } catch (error) {
            setVaccinationData(undefined);
            track('FETCH_VACCINE_INFO_FAILED');
            captureError(error as Error);
        } finally {
            setIsVaccinationDataLoading(false);
        }
    };

    const fetchPtVaccinationData = useCallback(async () => {
        if (!patient_oid || !doctor_oid) return;
        try {
            setIsDraftLoading(true);
            const res = await fetchPxDraft(appointment.aid, appointment.patient_oid || '');
            const vaccHistory = res.response.data.tool.allVaccines;
            const administedVaccines = res.response.data.tool.administeredVaccines;
            setVaccinationHistory(vaccHistory);
            setNewVaccs([...(administedVaccines || [])]);
            setDraftTool(res.response.data.tool || {});
        } catch (err) {
            console.error(err);
            setVaccinationHistory([]);
        } finally {
            setIsDraftLoading(false);
        }
    }, [patient_oid, doctor_oid]);

    useEffect(() => {
        fetchVaccineData();
    }, []);

    useEffect(() => {
        fetchPtVaccinationData();
    }, [fetchPtVaccinationData]);

    const onSaveVaccineRecordQueue = async (setLoading: (loading: boolean) => void) => {
        if (appointment && appointment.patient_oid) {
            const { doctor_oid, aid } = appointment;
            const followupDraft = followUpVaccine
                ? {
                      date: moment(followUpVaccine.date).toISOString(),
                      notes: `Next Vaccination Due - ${
                          diseaseIdToName[followUpVaccine.diseaseId].name
                      }: ${followUpVaccine.name} on ${moment(followUpVaccine.date).format(
                          'DD/MM/YYYY',
                      )}`,
                  }
                : undefined;

            try {
                setLoading(true);
                // await pushPtVaccHistory(doctor_oid, patient_oid, newVaccs);
                await pushPxDraft(generatePrescriptionId(doctor_oid, aid), aid, {
                    ...(draftTool || {}),
                    allVaccines: vaccinationHistory,
                    followup: followupDraft,
                    administeredVaccines: newVaccs,
                });

                closeMe();

                notification.success({
                    message: 'Vaccination data saved',
                    duration: 5,
                });
            } catch (err) {
                console.error(err);
                notification.error({
                    message: 'Could not save vaccine',
                    duration: 5,
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const onChange = (newVaccs: IVaccineStatus[], allVaccs: IVaccineStatus[]) => {
        const newIds = newVaccs.map((vacc) => vacc.id);
        setNewVaccs((prev) => {
            const filteredPrevs = prev.filter((prevVacc) => !newIds.includes(prevVacc.id));
            return [...filteredPrevs, ...newVaccs];
        });
        setVaccinationHistory(allVaccs);
    };

    const onDueFollowupSet = (vaccine: IVaccineStatus) => {
        const followUpDate = moment(vaccine.date);
        const currFollowupDate = moment(followUpVaccine?.date);

        if (!currFollowupDate || followUpDate.isBefore(currFollowupDate)) {
            setFollowUpVaccine(vaccine);
        }
    };

    const clearSelection = () => {
        const newVaccIds = newVaccs.map(newVacc => newVacc.id);
        console.log({
            newVaccIds,
        });

        setVaccinationHistory(prev => prev?.filter(vac => !newVaccIds.includes(vac.id)));
        setNewVaccs([]);
    };

    return (
        <RightPopUp closeMe={closeMe}>
            {isVaccinationDataLoading || isDraftLoading ? (
                <div className="w-full h-full flex justify-center items-center">
                    <AppLoader />
                </div>
            ) : (
                <VaccinationChart
                    vaccinationData={vaccinationData}
                    key={Object.keys(vaccinationData || ({} as Partial<VaccineInfo>)).length}
                    vaccinationHistory={vaccinationHistory || []}
                    clinic_id={appointment.clinic_id}
                    patientId={appointment.patient_oid}
                    onChange={onChange}
                    onSave={onSaveVaccineRecordQueue}
                    onDueFollowupSet={onDueFollowupSet}
                    clearSelection={clearSelection}
                    defaultSchedule={defaultSchedule}
                />
            )}
        </RightPopUp>
    );
};

export default ApptAddVaccinationRightPopUp;
